import * as React from 'react';

import { cn } from '@/lib/utils';

import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip-shadcn';

type TruncatedTooltipTextProps = {
  text?: string | null;
  className?: string;
  tooltipClassName?: string;
  lines?: 1 | 2;
};

function TruncatedTooltipText({
  text,
  className,
  tooltipClassName,
  lines = 1,
}: TruncatedTooltipTextProps) {
  const value = String(text || '').trim();
  const textRef = React.useRef<HTMLSpanElement | null>(null);
  const [isTruncated, setIsTruncated] = React.useState(false);

  React.useEffect(() => {
    if (!value) {
      setIsTruncated(false);
      return;
    }

    const node = textRef.current;
    if (!node) {
      setIsTruncated(false);
      return;
    }

    const checkTruncation = () => {
      const nextIsTruncated =
        node.scrollWidth > node.clientWidth || node.scrollHeight > node.clientHeight;
      setIsTruncated(nextIsTruncated);
    };

    checkTruncation();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(() => {
      checkTruncation();
    });
    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [value, lines]);

  if (!value) {
    return null;
  }

  const textNode = (
    <span
      ref={textRef}
      className={cn(
        'block w-full min-w-0 overflow-hidden text-ellipsis',
        lines === 2 ? 'line-clamp-2 whitespace-normal' : 'whitespace-nowrap',
        className
      )}
    >
      {value}
    </span>
  );

  if (!isTruncated) {
    return textNode;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {textNode}
      </TooltipTrigger>
      <TooltipContent className={cn('max-w-[420px] whitespace-normal break-words', tooltipClassName)}>
        {value}
      </TooltipContent>
    </Tooltip>
  );
}

export { TruncatedTooltipText };
